// src/components/pat/patEngine.js

import {
  extractSectorFromQuestion,
  getPublicSectorIntel,
} from "./patSectorIntel";

// -------------------------------------------------------
// ACTIONS
// -------------------------------------------------------

const PAGE_ACTIONS = {
  map: {
    label: "Choose a Postcode Sector",
    to: "/map",
  },

  pricing: {
    label: "View Pricing",
    to: "/pricing",
  },

  business: {
    label: "How Coupon Placement Works",
    to: "/coupon-ads",
  },

  signup: {
    label: "Create Business Account",
    to: "/signup?business=true",
  },

  referrals: {
    label: "Refer a Business",
    to: "/referrals",
  },

  terms: {
    label: "Read Terms",
    to: "/terms",
  },

  privacy: {
    label: "Privacy Policy",
    to: "/privacy",
  },

  cookies: {
    label: "Cookie Policy",
    to: "/cookies",
  },
};

// -------------------------------------------------------
// BUSINESS TYPES
// -------------------------------------------------------

const BUSINESS_TYPES = [
  {
    id: "vehicle",

    terms: [
      "car valeting",
      "car valet",
      "valeting",
      "valeter",
      "detailer",
      "detailing",
      "car detailing",
      "mobile valeter",
      "car wash",
      "garage",
      "mechanic",
      "mot",
      "tyres",
      "vehicle",
      "car care",
      "bodyshop",
      "body shop",
    ],

    label: "vehicle-service",

    friendlyLabel:
      "vehicle-service business",

    suggestion:
      "A first-booking coupon, seasonal service coupon or fixed-price package could give households a straightforward reason to scan and respond.",
  },

  {
    id: "trade",

    terms: [
      "plumber",
      "electrician",
      "builder",
      "roofer",
      "joiner",
      "carpenter",
      "decorator",
      "painter",
      "handyman",
      "heating engineer",
      "boiler",
      "locksmith",
      "trade",
      "tradesman",
      "property maintenance",
    ],

    label: "trade",

    friendlyLabel:
      "trade business",

    suggestion:
      "A fixed-price service, call-out incentive or seasonal maintenance coupon could give households a clear reason to keep and scan the coupon.",
  },

  {
    id: "food",

    terms: [
      "restaurant",
      "cafe",
      "café",
      "takeaway",
      "pizza",
      "burger",
      "bakery",
      "food",
      "coffee",
      "pub",
      "bar",
      "hotel",
      "hospitality",
      "fish and chips",
      "chippy",
    ],

    label:
      "food or hospitality",

    friendlyLabel:
      "food or hospitality business",

    suggestion:
      "A first-order coupon, set-menu coupon or time-limited redemption could give households a simple reason to visit or order.",
  },

  {
    id: "beauty",

    terms: [
      "hairdresser",
      "hair salon",
      "barber",
      "beauty",
      "nails",
      "nail salon",
      "massage",
      "spa",
      "aesthetics",
      "beautician",
      "tanning",
      "lashes",
      "brows",
    ],

    label:
      "hair or beauty",

    friendlyLabel:
      "hair or beauty business",

    suggestion:
      "A first-appointment coupon, treatment upgrade or introductory package could create a clear redemption journey.",
  },

  {
    id: "fitness",

    terms: [
      "gym",
      "fitness",
      "personal trainer",
      "yoga",
      "pilates",
      "sports club",
      "martial arts",
      "boxing",
      "dance school",
    ],

    label: "fitness",

    friendlyLabel:
      "fitness business",

    suggestion:
      "A trial session, joining incentive or introductory package could give households a clear reason to respond.",
  },

  {
    id: "home",

    terms: [
      "cleaner",
      "cleaning",
      "window cleaner",
      "gardener",
      "gardening",
      "landscaper",
      "landscaping",
      "lawn",
      "pest control",
      "removals",
      "home service",
      "oven cleaning",
      "carpet cleaning",
      "pressure washing",
      "gutter cleaning",
    ],

    label: "home-service",

    friendlyLabel:
      "home-service business",

    suggestion:
      "A first-service coupon, seasonal package or fixed-price introductory service could work well as the redemption incentive.",
  },

  {
    id: "retail",

    terms: [
      "shop",
      "retail",
      "store",
      "boutique",
      "clothes",
      "clothing",
      "furniture",
      "pet shop",
      "florist",
      "gift shop",
      "jewellery",
    ],

    label: "retail",

    friendlyLabel:
      "retail business",

    suggestion:
      "A spend-based coupon, product-category benefit or first-purchase incentive could create a clear reason to visit.",
  },

  {
    id: "professional",

    terms: [
      "accountant",
      "solicitor",
      "mortgage",
      "financial adviser",
      "estate agent",
      "consultant",
      "professional service",
      "insurance",
      "bookkeeper",
      "legal",
    ],

    label:
      "professional-service",

    friendlyLabel:
      "professional-service business",

    suggestion:
      "An initial consultation, review or fixed-price introductory service could provide a clear response route.",
  },

  {
    id: "pet",

    terms: [
      "dog groomer",
      "dog grooming",
      "pet groomer",
      "pet grooming",
      "dog walker",
      "pet sitter",
      "pet care",
      "vet",
      "veterinary",
      "kennels",
    ],

    label: "pet-service",

    friendlyLabel:
      "pet-service business",

    suggestion:
      "A first-booking coupon, grooming package or introductory pet-care service could give households a clear reason to respond.",
  },
];

// -------------------------------------------------------
// RESPONSE VARIATIONS
// -------------------------------------------------------

const SIMPLE_GREETINGS = [
  "Hi 👋 It’s lovely to hear from you. What can I help you with today?",

  "Hello 👋 Welcome to Postcode.Coupons. What would you like to know?",

  "Hi there 👋 How can I help today?",
];

const HOW_ARE_YOU_RESPONSES = [
  "Hi 👋 I’m good, thank you! How are you? What brings you to Postcode.Coupons today?",

  "Hello 👋 I’m doing well, thanks for asking! Are you having a look around, or is there something I can help you with?",

  "Hi 👋 I’m good thanks! How can I help you today?",
];

const THANKS = [
  "You’re very welcome 😊 Is there anything else you’d like help with?",

  "No problem at all. Is there anything else you’d like to check?",

  "Happy to help 😊 What else would you like to know?",
];

const FALLBACKS = [
  "I’m not quite sure which part you mean. Could you give me a little more detail?",

  "I want to make sure I give you the right answer. Are you asking about coupons, pricing, postcode sectors, delivery, QR redemption or referrals?",

  "Could you tell me a little more about what you’re trying to work out?",
];

// -------------------------------------------------------
// GENERAL HELPERS
// -------------------------------------------------------

function normalise(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function containsAny(
  text,
  terms
) {
  return terms.some((term) =>
    text.includes(term)
  );
}

function choose(
  items,
  seed = ""
) {
  if (!items.length) {
    return "";
  }

  const total = String(seed)
    .split("")
    .reduce(
      (sum, character) =>
        sum +
        character.charCodeAt(0),
      0
    );

  return items[
    total % items.length
  ];
}

function detectBusiness(text) {
  return BUSINESS_TYPES.find(
    (business) =>
      containsAny(
        text,
        business.terms
      )
  );
}

// -------------------------------------------------------
// CURRENT-CONVERSATION MEMORY
// -------------------------------------------------------

function getUserMessages(
  messages
) {
  return messages.filter(
    (message) =>
      message.role === "user"
  );
}

function getUserConversationText(
  messages
) {
  return getUserMessages(
    messages
  )
    .slice(-12)
    .map((message) =>
      normalise(
        message.text ||
          message.content
      )
    )
    .join(" ");
}

function getLastBusiness(
  messages
) {
  return detectBusiness(
    getUserConversationText(
      messages
    )
  );
}

function getLastUserSector(
  messages
) {
  const userMessages =
    getUserMessages(messages);

  for (
    let index =
      userMessages.length - 1;
    index >= 0;
    index -= 1
  ) {
    const message =
      userMessages[index];

    const sector =
      extractSectorFromQuestion(
        message.text ||
          message.content
      );

    if (sector) {
      return sector;
    }
  }

  return null;
}

function conversationHasCouponIdea(
  messages
) {
  const text =
    getUserConversationText(
      messages
    );

  return containsAny(text, [
    "discount",
    "percent off",
    "% off",
    "£5 off",
    "£10 off",
    "£20 off",
    "free consultation",
    "free trial",
    "first booking",
    "first order",
    "introductory",
    "fixed price",
    "special package",
    "coupon idea",
  ]);
}

// -------------------------------------------------------
// SMALL TALK
// -------------------------------------------------------

function isHowAreYou(text) {
  return containsAny(text, [
    "how are you",
    "how's it going",
    "hows it going",
    "how are things",
    "you alright",
    "are you well",
    "how are u",
  ]);
}

function isGreeting(text) {
  return /^(hi|hello|hey|hiya|morning|good morning|afternoon|good afternoon|evening|good evening|hi pat|hello pat)\b/.test(
    text
  );
}

function isThanks(text) {
  return containsAny(text, [
    "thank you",
    "thanks",
    "cheers",
    "nice one",
    "that's helpful",
    "thats helpful",
  ]);
}

function isShortConfirmation(
  text
) {
  return /^(yes|yeah|yep|ok|okay|sounds good|great|right|correct|i do|i have|sure|please|yes please)[!. ]*$/.test(
    text
  );
}

function hasBuyingIntent(
  text
) {
  return containsAny(text, [
    "i'm interested",
    "i am interested",
    "sounds good",
    "let's do it",
    "lets do it",
    "sign me up",
    "i want to do it",
    "i want one",
    "i want to buy",
    "i want to place",
    "how do i buy",
    "how do i book",
    "where do i start",
    "what do i do next",
    "what's next",
    "whats next",
    "ready to start",
    "ready to book",
    "go ahead",
    "start now",
  ]);
}

// -------------------------------------------------------
// FORMATTERS
// -------------------------------------------------------

function formatNumber(value) {
  return new Intl.NumberFormat(
    "en-GB"
  ).format(value);
}

function formatPrice(value) {
  return new Intl.NumberFormat(
    "en-GB",
    {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(value);
}

// -------------------------------------------------------
// HOUSEHOLD CALCULATOR
// -------------------------------------------------------

function extractHouseholdCount(
  text
) {
  const compact =
    text.replace(/,/g, "");

  const patterns = [
    /(?:for|on|with|reach|covering)\s+(\d{3,7})\s*(?:households?|houses?|homes?)/i,

    /(\d{3,7})\s*(?:households?|houses?|homes?)/i,

    /(?:cost|price|how much).*?(\d{3,7})/i,

    /^(\d{3,7})$/i,
  ];

  for (const pattern of patterns) {
    const match =
      compact.match(pattern);

    if (!match) {
      continue;
    }

    const amount =
      Number.parseInt(
        match[1],
        10
      );

    if (
      Number.isFinite(amount) &&
      amount > 0 &&
      amount <= 10000000
    ) {
      return amount;
    }
  }

  return null;
}

// -------------------------------------------------------
// NEXT HELPFUL QUESTION
// -------------------------------------------------------

function getNextQuestion({
  business,
  sector,
  hasCouponIdea,
}) {
  if (!business) {
    return "What type of business do you run?";
  }

  if (!sector) {
    return "Is there a postcode sector you’re interested in reaching?";
  }

  if (!hasCouponIdea) {
    return "Have you thought about what you’d like to include in your coupon?";
  }

  return "Would you like to review this postcode sector on the map?";
}

// -------------------------------------------------------
// INTENT SCORING
// -------------------------------------------------------

function buildScores(text) {
  const scores = {
    pricing: 0,
    business: 0,
    howItWorks: 0,
    sector: 0,
    campaign: 0,
    delivery: 0,
    redemption: 0,
    analytics: 0,
    referrals: 0,
    refund: 0,
    signup: 0,
    consumer: 0,
    privacy: 0,
    cookies: 0,
    buyingIntent: 0,
    objectionSocial: 0,
    objectionPrice: 0,
    objectionResults: 0,
    objectionScans: 0,
    objectionDelay: 0,
    objectionPro: 0,
    trialSector: 0,
  };

  if (
    hasBuyingIntent(text)
  ) {
    scores.buyingIntent += 20;
  }

  if (
    containsAny(text, [
      "price",
      "pricing",
      "cost",
      "how much",
      "1p",
      "one pence",
      "one penny",
      "quote",
    ])
  ) {
    scores.pricing += 10;
  }

  if (
    containsAny(text, [
      "too expensive",
      "expensive",
      "costs too much",
      "can't afford",
      "cannot afford",
      "not worth the price",
    ])
  ) {
    scores.objectionPrice += 18;
  }

  if (
    containsAny(text, [
      "my business",
      "help my business",
      "work for me",
      "suitable",
      "good for",
      "i run",
      "i own",
      "our business",
      "customers",
      "business",
    ])
  ) {
    scores.business += 11;
  }

  if (
    containsAny(text, [
      "how does it work",
      "how it works",
      "what is postcode",
      "explain platform",
      "what do you do",
      "what does it do",
    ])
  ) {
    scores.howItWorks += 12;
  }

  if (
    containsAny(text, [
      "postcode",
      "sector",
      "map",
      "area",
      "which sector",
      "find sector",
      "sector intel",
      "sector information",
    ])
  ) {
    scores.sector += 9;
  }

  if (
    containsAny(text, [
      "campaign",
      "capacity",
      "fill",
      "filled",
      "printing",
      "when print",
      "how long",
      "minimum",
      "slots",
      "positions",
    ])
  ) {
    scores.campaign += 10;
  }

  if (
    containsAny(text, [
      "takes too long",
      "taking too long",
      "what if it never fills",
      "what if it doesn't fill",
      "how long will i wait",
    ])
  ) {
    scores.objectionDelay += 18;
  }

  if (
    containsAny(text, [
      "delivery",
      "deliver",
      "royal mail",
      "posted",
      "door",
      "arrive",
    ])
  ) {
    scores.delivery += 11;
  }

  if (
    containsAny(text, [
      "redeem",
      "redemption",
      "qr",
      "qr code",
      "coupon page",
      "landing page",
      "scan",
    ])
  ) {
    scores.redemption += 10;
  }

  if (
    containsAny(text, [
      "nobody scans",
      "no one scans",
      "no scans",
      "what if nobody scans",
      "what if no one scans",
    ])
  ) {
    scores.objectionScans += 18;
  }

  if (
    containsAny(text, [
      "analytics",
      "pro",
      "£19",
      "19 per month",
      "impressions",
      "scan rate",
      "csv",
      "pdf report",
      "reporting",
    ])
  ) {
    scores.analytics += 10;
  }

  if (
    containsAny(text, [
      "do i need pro",
      "need pro",
      "why pro",
      "why do i need pro",
      "is pro worth it",
    ])
  ) {
    scores.objectionPro += 20;
  }

  if (
    containsAny(text, [
      "refer",
      "referral",
      "give £10",
      "get £10",
      "ten pound",
    ])
  ) {
    scores.referrals += 11;
  }

  if (
    containsAny(text, [
      "refund",
      "cancel",
      "money back",
      "non-refundable",
      "non refundable",
    ])
  ) {
    scores.refund += 11;
  }

  if (
    containsAny(text, [
      "sign up",
      "signup",
      "join",
      "get started",
      "create account",
      "place my coupon",
      "book",
      "buy",
    ])
  ) {
    scores.signup += 12;
  }

  if (
    containsAny(text, [
      "try one sector",
      "start with one",
      "one sector",
      "test one sector",
      "small test",
      "test it first",
    ])
  ) {
    scores.trialSector += 17;
  }

  if (
    containsAny(text, [
      "find coupons",
      "coupon near",
      "coupons for me",
      "looking for coupon",
      "redeem a coupon",
    ])
  ) {
    scores.consumer += 10;
  }

  if (
    containsAny(text, [
      "privacy",
      "personal data",
      "data protection",
      "my data",
    ])
  ) {
    scores.privacy += 11;
  }

  if (
    containsAny(text, [
      "cookie",
      "cookies",
      "tracking",
      "browser storage",
    ])
  ) {
    scores.cookies += 11;
  }

  if (
    containsAny(text, [
      "facebook",
      "social media",
      "instagram",
      "google ads",
      "instead of facebook",
    ])
  ) {
    scores.objectionSocial += 17;
  }

  if (
    containsAny(text, [
      "will it work",
      "does it work",
      "will this work",
      "how many customers",
      "how many bookings",
      "guarantee",
      "guaranteed",
      "what results",
      "roi",
      "waste of money",
      "worth it",
      "why should i",
      "why would i",
    ])
  ) {
    scores.objectionResults += 17;
  }

  return scores;
}

function topIntent(scores) {
  return Object.entries(
    scores
  ).sort(
    (first, second) =>
      second[1] - first[1]
  )[0];
}

// -------------------------------------------------------
// LIVE SECTOR INTELLIGENCE
// -------------------------------------------------------

async function answerSectorQuestion({
  question,
  business,
  messages,
}) {
  const sector =
    extractSectorFromQuestion(
      question
    );

  if (!sector) {
    return null;
  }

  const result =
    await getPublicSectorIntel(
      sector
    );

  if (
    result.reason ===
    "lookup-failed"
  ) {
    return {
      text:
        `Sorry, I couldn’t retrieve the live information for ${sector} just now.\n\n` +
        "You can still check the sector directly on the postcode map.",

      action:
        PAGE_ACTIONS.map,
    };
  }

  if (!result.found) {
    return {
      text:
        `I couldn’t find a household count for ${sector} in the current sector data.\n\n` +
        "It may be worth checking the sector spelling or searching for it on the map.",

      action:
        PAGE_ACTIONS.map,
    };
  }

  const hasCouponIdea =
    conversationHasCouponIdea(
      messages
    );

  const nextQuestion =
    getNextQuestion({
      business,

      sector:
        result.sector,

      hasCouponIdea,
    });

  const businessText =
    business
      ? `\n\nFor your ${business.friendlyLabel}, ${business.suggestion.toLowerCase()}`
      : "";

  return {
    text:
      `${result.sector} currently shows ${formatNumber(
        result.households
      )} households in the Postcode.Coupons sector data.\n\n` +
      `At 1p per household, one coupon placement would be ${formatPrice(
        result.price
      )}.` +
      businessText +
      `\n\n${nextQuestion}`,

    action:
      PAGE_ACTIONS.map,

    context: {
      sector:
        result.sector,

      households:
        result.households,

      price:
        result.price,
    },
  };
}

// -------------------------------------------------------
// ANSWER ENGINE
// -------------------------------------------------------

export async function answerPat({
  question,
  messages = [],
  pathname = "/",
}) {
  const text =
    normalise(question);

  if (!text) {
    return {
      text:
        "Type a question whenever you’re ready 😊",
    };
  }

  // Memory comes only from visitor messages
  // in the current open conversation.
  const detectedBusiness =
    detectBusiness(text) ||
    getLastBusiness(messages);

  const rememberedSector =
    getLastUserSector(messages);

  const hasCouponIdea =
    conversationHasCouponIdea(
      messages
    );

  // -----------------------------------------------------
  // FRIENDLY SMALL TALK FIRST
  // -----------------------------------------------------

  if (isHowAreYou(text)) {
    return {
      text:
        choose(
          HOW_ARE_YOU_RESPONSES,
          text
        ),
    };
  }

  if (isGreeting(text)) {
    return {
      text:
        choose(
          SIMPLE_GREETINGS,
          text
        ),
    };
  }

  if (isThanks(text)) {
    return {
      text:
        choose(
          THANKS,
          text
        ),
    };
  }

  // -----------------------------------------------------
  // LIVE SECTOR LOOKUP
  // -----------------------------------------------------

  const sectorAnswer =
    await answerSectorQuestion({
      question,

      business:
        detectedBusiness,

      messages,
    });

  if (sectorAnswer) {
    return sectorAnswer;
  }

  // -----------------------------------------------------
  // MANUAL HOUSEHOLD CALCULATION
  // -----------------------------------------------------

  const householdCount =
    extractHouseholdCount(
      text
    );

  if (householdCount) {
    const price =
      householdCount * 0.01;

    const nextQuestion =
      getNextQuestion({
        business:
          detectedBusiness,

        sector:
          rememberedSector,

        hasCouponIdea,
      });

    return {
      text:
        `At 1p per household, ${formatNumber(
          householdCount
        )} households would cost ${formatPrice(
          price
        )} for one postcode-sector coupon placement.\n\n` +
        "The final sector price uses the live household count recorded for that sector.\n\n" +
        nextQuestion,

      action:
        PAGE_ACTIONS.map,
    };
  }

  const scores =
    buildScores(text);

  const [intent, score] =
    topIntent(scores);

  // -----------------------------------------------------
  // SHORT CONFIRMATION
  // -----------------------------------------------------

  if (
    isShortConfirmation(
      text
    )
  ) {
    if (
      rememberedSector &&
      detectedBusiness &&
      hasCouponIdea
    ) {
      return {
        text:
          "Lovely. You’ve got the main pieces in place: your business, a postcode sector and a coupon idea.\n\n" +
          "You can now review the sector’s live campaign information on the map before proceeding.",

        action:
          PAGE_ACTIONS.map,
      };
    }

    const nextQuestion =
      getNextQuestion({
        business:
          detectedBusiness,

        sector:
          rememberedSector,

        hasCouponIdea,
      });

    return {
      text:
        `Great 😊 ${nextQuestion}`,

      action:
        rememberedSector
          ? PAGE_ACTIONS.business
          : PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // GENUINE BUYING INTENT
  // -----------------------------------------------------

  if (
    intent ===
    "buyingIntent"
  ) {
    if (rememberedSector) {
      return {
        text:
          `That sounds good. You already have ${rememberedSector} in mind.\n\n` +
          "The next step is to review the sector’s live campaign information on the map and continue when you’re happy.",

        action:
          PAGE_ACTIONS.map,
      };
    }

    return {
      text:
        "That sounds good 😊 The next step is to choose the postcode sector you want to reach.\n\n" +
        "The live household count will determine the placement price at 1p per household.\n\n" +
        "Is there a postcode sector you already have in mind?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // UNKNOWN OR CASUAL MESSAGE
  // -----------------------------------------------------

  if (score === 0) {
    if (detectedBusiness) {
      const nextQuestion =
        getNextQuestion({
          business:
            detectedBusiness,

          sector:
            rememberedSector,

          hasCouponIdea,
        });

      return {
        text:
          `Thanks, that helps. For your ${detectedBusiness.friendlyLabel}, ${detectedBusiness.suggestion.toLowerCase()}\n\n` +
          nextQuestion,

        action:
          rememberedSector
            ? PAGE_ACTIONS.business
            : PAGE_ACTIONS.map,
      };
    }

    return {
      text:
        choose(
          FALLBACKS,
          text
        ),
    };
  }

  // -----------------------------------------------------
  // PRICE OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionPrice"
  ) {
    return {
      text:
        "I understand. The total can look different depending on the sector’s household count.\n\n" +
        "Starting with one postcode sector and one clear coupon lets you keep the first campaign focused without committing to several sectors at once.\n\n" +
        (
          rememberedSector
            ? "Have you thought about what you’d like to include in the coupon?"
            : "Is there a postcode sector you’d like me to check?"
        ),

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // RESULTS OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionResults"
  ) {
    return {
      text:
        "That’s a fair question. No responsible platform can promise a particular number of bookings, customers or redemptions.\n\n" +
        "Postcode.Coupons gives you a selected postcode-sector campaign, a printed household coupon and a QR redemption route. The coupon itself still needs a clear reason for somebody to respond.\n\n" +
        "What kind of benefit were you thinking of including?",

      action:
        PAGE_ACTIONS.business,
    };
  }

  // -----------------------------------------------------
  // SCAN OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionScans"
  ) {
    return {
      text:
        "That’s understandable. A QR code alone won’t make somebody respond.\n\n" +
        "The coupon needs a clear benefit, simple wording and an obvious reason to scan. The redemption page then makes the response route easier.\n\n" +
        "What would you like households to receive from the coupon?",

      action:
        PAGE_ACTIONS.business,
    };
  }

  // -----------------------------------------------------
  // DELAY OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionDelay"
  ) {
    return {
      text:
        "That’s a fair concern. A booking doesn’t mean immediate printing because the campaign must reach its required capacity, and the necessary payments and coupon content must be complete.\n\n" +
        "The sector page is the right place to review the current campaign before purchasing.\n\n" +
        "Which postcode sector are you considering?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // PRO OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionPro"
  ) {
    return {
      text:
        "You don’t need Pro simply to place a coupon.\n\n" +
        "QR Analytics Pro is the optional £19-per-month reporting upgrade for impressions, scans, scan rate, trends and downloadable reports.\n\n" +
        "Are you mainly interested in placing the coupon first, or would the extra QR reporting be useful too?",

      action:
        PAGE_ACTIONS.pricing,
    };
  }

  // -----------------------------------------------------
  // SOCIAL MEDIA OBJECTION
  // -----------------------------------------------------

  if (
    intent ===
    "objectionSocial"
  ) {
    return {
      text:
        "That’s a good comparison. Postcode.Coupons and social media do different jobs.\n\n" +
        "Social-media visibility depends on people seeing content in a feed. Postcode.Coupons centres the campaign around the postcode sector you choose, with a printed household coupon and QR response route.\n\n" +
        "You could use both rather than relying entirely on one channel.\n\n" +
        "Is there a postcode sector you’re interested in reaching?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // ONE-SECTOR START
  // -----------------------------------------------------

  if (
    intent ===
    "trialSector"
  ) {
    return {
      text:
        "Yes, starting with one postcode sector is a sensible way to keep the first campaign focused.\n\n" +
        "You can check the sector’s live household count and placement price before proceeding.\n\n" +
        "Which postcode sector are you considering?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // BUSINESS FIT
  // -----------------------------------------------------

  if (
    intent === "business"
  ) {
    if (detectedBusiness) {
      const nextQuestion =
        getNextQuestion({
          business:
            detectedBusiness,

          sector:
            rememberedSector,

          hasCouponIdea,
        });

      return {
        text:
          `Thanks, that gives me something useful to work with. Postcode.Coupons could be relevant to your ${detectedBusiness.friendlyLabel}.\n\n` +
          "You choose the postcode sector you want to reach, and the printed coupon can connect through QR to its redemption page.\n\n" +
          `${detectedBusiness.suggestion}\n\n` +
          nextQuestion,

        action:
          rememberedSector
            ? PAGE_ACTIONS.business
            : PAGE_ACTIONS.map,
      };
    }

    return {
      text:
        "Postcode.Coupons helps businesses choose postcode sectors, place coupons in those campaigns and connect the printed coupon to a QR redemption page.\n\n" +
        "It works best when the coupon gives households one clear reason to respond.\n\n" +
        "What type of business do you run?",

      action:
        PAGE_ACTIONS.business,
    };
  }

  // -----------------------------------------------------
  // PRICING
  // -----------------------------------------------------

  if (
    intent === "pricing"
  ) {
    return {
      text:
        "Coupon placement normally costs 1p per household per postcode sector.\n\n" +
        "For example, 5,000 households would cost £50 and 8,000 households would cost £80.\n\n" +
        "You can give me a household count or a postcode sector and I’ll work it out for you.",

      action:
        PAGE_ACTIONS.pricing,
    };
  }

  // -----------------------------------------------------
  // HOW IT WORKS
  // -----------------------------------------------------

  if (
    intent ===
    "howItWorks"
  ) {
    return {
      text:
        "A business chooses a postcode sector and places a coupon in that sector’s campaign. The price is based on the live household count at 1p per household.\n\n" +
        "Once the campaign reaches its required capacity and the necessary coupon content and payments are complete, it proceeds towards printing and household delivery.\n\n" +
        "The printed coupon can also connect through QR to a digital redemption page.\n\n" +
        "Are you looking at this for a business?",

      action:
        PAGE_ACTIONS.business,
    };
  }

  // -----------------------------------------------------
  // SECTOR
  // -----------------------------------------------------

  if (
    intent === "sector"
  ) {
    return {
      text:
        "Of course. Tell me the postcode sector you’re considering and I can check its live household count and calculate the placement price.\n\n" +
        "For example: Tell me about PR4.",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // CAMPAIGN
  // -----------------------------------------------------

  if (
    intent === "campaign"
  ) {
    return {
      text:
        "Each postcode-sector campaign has a required capacity based on its format, printing requirements and distribution plan.\n\n" +
        "The campaign proceeds towards printing when capacity is filled and the necessary payments and coupon content are complete. A booking therefore doesn’t guarantee immediate printing.\n\n" +
        "Is there a particular postcode sector you’d like to check?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // DELIVERY
  // -----------------------------------------------------

  if (
    intent === "delivery"
  ) {
    return {
      text:
        "Completed campaigns are scheduled for printing and household delivery, which can be carried out through Royal Mail.\n\n" +
        "Timing can depend on campaign capacity, coupon content, print scheduling, production and postal operations, so I won’t promise an exact delivery date.\n\n" +
        "Do you already have a postcode sector in mind?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // REDEMPTION
  // -----------------------------------------------------

  if (
    intent === "redemption"
  ) {
    return {
      text:
        "The QR code can open a mobile-friendly redemption page containing the coupon, business details, redemption instructions and applicable conditions.\n\n" +
        "That gives somebody holding the printed coupon a clear digital action.\n\n" +
        "Are you thinking of a particular coupon for your business?",

      action:
        PAGE_ACTIONS.business,
    };
  }

  // -----------------------------------------------------
  // ANALYTICS
  // -----------------------------------------------------

  if (
    intent === "analytics"
  ) {
    return {
      text:
        "QR Analytics Pro is the optional £19-per-month upgrade.\n\n" +
        "It can include impressions, QR scans, scan rate, slot-level performance, trends and downloadable reports. It’s separate from the coupon-placement price.\n\n" +
        "Would the extra QR reporting be useful for your business?",

      action:
        PAGE_ACTIONS.pricing,
    };
  }

  // -----------------------------------------------------
  // REFERRALS
  // -----------------------------------------------------

  if (
    intent === "referrals"
  ) {
    return {
      text:
        "The tracked referral is Give £10. Get £10.\n\n" +
        "When a tracked referral qualifies, the referring and referred businesses each receive £10 account credit towards future coupon placements.\n\n" +
        "A signed-in business account is required.\n\n" +
        "Do you already have a business account?",

      action:
        PAGE_ACTIONS.referrals,
    };
  }

  // -----------------------------------------------------
  // REFUNDS
  // -----------------------------------------------------

  if (
    intent === "refund"
  ) {
    return {
      text:
        "Business bookings, coupon placements, account credits, subscriptions and paid features are non-refundable once purchased, except where required by law.\n\n" +
        "The full contractual wording is available in the Terms of Service.",

      action:
        PAGE_ACTIONS.terms,
    };
  }

  // -----------------------------------------------------
  // SIGNUP
  // -----------------------------------------------------

  if (
    intent === "signup"
  ) {
    if (rememberedSector) {
      return {
        text:
          `You already have ${rememberedSector} in mind, so the next step is to review that sector’s live information on the map and continue when you’re ready.`,

        action:
          PAGE_ACTIONS.map,
      };
    }

    return {
      text:
        "Lovely. The next step is to choose the postcode sector you want and review its current household count and placement price.\n\n" +
        "Is there a postcode sector you already have in mind?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // CONSUMER COUPONS
  // -----------------------------------------------------

  if (
    intent === "consumer"
  ) {
    return {
      text:
        "You can use Postcode.Coupons to find coupons connected to postcode sectors and participating businesses.\n\n" +
        "Which postcode sector would you like to explore?",

      action:
        PAGE_ACTIONS.map,
    };
  }

  // -----------------------------------------------------
  // PRIVACY
  // -----------------------------------------------------

  if (
    intent === "privacy"
  ) {
    return {
      text:
        "The Privacy Policy explains how Postcode.Coupons handles personal information.",

      action:
        PAGE_ACTIONS.privacy,
    };
  }

  // -----------------------------------------------------
  // COOKIES
  // -----------------------------------------------------

  if (
    intent === "cookies"
  ) {
    return {
      text:
        "The Cookie Policy explains cookies and browser-storage use on Postcode.Coupons.",

      action:
        PAGE_ACTIONS.cookies,
    };
  }

  return {
    text:
      choose(
        FALLBACKS,
        text
      ),
  };
}

// -------------------------------------------------------
// PAGE-AWARE INTRO
// -------------------------------------------------------

export function getPatIntro(
  pathname
) {
  if (
    pathname === "/pricing"
  ) {
    return "I can help with pricing 😊 Give me a household count or postcode sector and I’ll work it out for you.";
  }

  if (
    pathname ===
    "/coupon-ads"
  ) {
    return "Thinking about placing a coupon? Tell me a little about your business and I’ll help you work through it.";
  }

  if (
    pathname === "/map"
  ) {
    return "Need help choosing a postcode sector? Give me a sector or tell me about your business.";
  }

  if (
    pathname.startsWith(
      "/coupon"
    ) ||
    pathname.startsWith(
      "/flyer"
    ) ||
    pathname.startsWith(
      "/r/"
    )
  ) {
    return "Need help with this coupon or how QR redemption works? Just ask.";
  }

  return "Hi 👋 I can help with coupons, business placement, postcode sectors, pricing, delivery, QR redemption, Pro analytics and referrals.";
}